import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Auth } from 'src/app/core/common/auth';
import { RescruitmentService } from 'src/app/data/rescruitment.service';
import { BaseComponent } from 'src/app/shared/components/base-component/base.component';

export interface INotification {
  id: number;
  typeId: number;
  requestId: number;
  titleKey: string;
  messageKey: string;
  date: Date;
  isRead: boolean;
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css'],
  standalone: true,
  imports: [CommonModule, TranslateModule],
})
export class NotificationsComponent extends BaseComponent implements OnInit {
  isOpen = false;
  dropdownStyle: { [key: string]: string } = {};

  @ViewChild('bellButton', { static: true }) bellButton!: ElementRef<HTMLButtonElement>;

  notifications: INotification[] = [];

  constructor(private elementRef: ElementRef, private router: Router, private recService: RescruitmentService, private auth: Auth) {
    super();
  }

  override async ngOnInit() {
    super.ngOnInit();
    const isAuthenticated = await this.auth.isAuthenticated();
    if (!isAuthenticated) {
      return;
    }
    this.recService.getNotifications().subscribe(notifications => {
      const isAr = this.isRtl();
      this.notifications = notifications.map(n => ({
        id: +n.id,
        typeId: +n.procedureId,
        requestId: +n.requestId,
        titleKey: +n.procedureId === 122 ? 'Notifications.RecruitmentTitle' : +n.procedureId === 63 ? 'Notifications.ManPowerTitle' : '',
        date: new Date(n.createDate),
        isRead: n.isRead,
        messageKey: this.resolveNotificationMessage(n.name, n.requestId, !!isAr),
      }));
    });
  }

  private resolveNotificationMessage(name: string, requestId: string, isAr: boolean): string {
    if (isAr) {
      switch (name) {
        case 'Documents Uploading Recruitment Request':
          return `طلب الاستقدام رقم #${requestId} جاهز الان`;
        case 'Completed Recruitment Request':
          return `تم الموفقة علي طلب الاستقدام رقم #${requestId}`;
        case 'Approved Labour Request':
          return `تم اعتماد طلب عمالة من الوكالة رقم #${requestId}`;
        case 'Completed Labour Request':
          return `تم الموفقة علي طلب عمالة من الوكالة رقم #${requestId}`;
        default:
          return name;
      }
    } else {
      switch (name) {
        case 'Documents Uploading Recruitment Request':
          return `Labour request #${requestId} is ready now`;
        case 'Completed Recruitment Request':
          return `Labour request #${requestId} has been approved`;
        case 'Approved Labour Request':
          return `Agency manpower request #${requestId} has been approved`;
        case 'Completed Labour Request':
          return `Agency manpower request #${requestId} has been completed`;
        default:
          return name;
      }
    }
  }

  get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.updateDropdownPosition();
    }
  }

  markAsRead(notification: INotification): void {
    if (!notification.isRead) {
      this.recService.markNotificationAsRead(notification.id).subscribe(() => {
        notification.isRead = true;
        if (+notification.typeId === 122) {
          this.router.navigate(['/recruitment/Details', notification.requestId]);
        } else if (+notification.typeId === 63) {
          this.router.navigate(['/man-power/Details', notification.requestId]);
        }
      });
    }
  }

  markAllAsRead(): void {
    this.notifications.forEach(n => {
      if (!n.isRead) {
        this.recService.markNotificationAsRead(n.id).subscribe(() => {
          n.isRead = true;
        });
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen) {
      this.updateDropdownPosition();
    }
  }

  private updateDropdownPosition(): void {
    const rect = this.bellButton.nativeElement.getBoundingClientRect();
    const isRtl = this.isRtl();

    this.dropdownStyle = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      ...(isRtl ? { right: `${window.innerWidth - rect.right}px` } : { left: `${rect.left}px` }),
    };
  }

  getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return this.ui.translate.instant(this.translation.Notifications.JustNow) as string;
    } else if (diffMins < 60) {
      return `${diffMins} ${this.ui.translate.instant(this.translation.Notifications.MinutesAgo)}`;
    } else if (diffHours < 24) {
      return `${diffHours} ${this.ui.translate.instant(this.translation.Notifications.HoursAgo)}`;
    } else {
      return `${diffDays} ${this.ui.translate.instant(this.translation.Notifications.DaysAgo)}`;
    }
  }
}
